import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CreatePracticeSessionRequest, PracticeLibraryItem, PracticeRecommendation, PracticeType } from "@repo/shared-types";
import { usePracticeDashboard } from "../hooks/usePractice";
import { fieldStyle } from "../lib/ui";

const PRACTICE_TYPES: { value: PracticeType; label: string; icon: string; color: string }[] = [
  { value: "meditation", label: "Meditation",    icon: "🧘", color: "#7c3aed" },
  { value: "yoga",      label: "Yoga",          icon: "🌿", color: "#059669" },
  { value: "breathing", label: "Breathing",     icon: "🌬️", color: "#0284c7" },
  { value: "reading",   label: "Sacred Reading", icon: "📖", color: "#b45309" },
  { value: "other",     label: "Other",         icon: "✨", color: "#db2777" },
];

const QUICK_DURATIONS = [5, 10, 20, 30, 45, 60];

const DAILY_QUOTES = [
  "The mind is everything. What you think you become. — Buddha",
  "Yoga is the journey of the self, through the self, to the self. — Bhagavad Gita",
  "In the middle of difficulty lies opportunity for stillness.",
  "One breath at a time, one day at a time — this is sadhana.",
  "Peace comes from within. Do not seek it without. — Siddhartha Gautama",
  "Let go of what has passed. Let go of what may come. Rest in what is.",
  "Silence is the language of God; all else is poor translation. — Rumi",
];

function getDailyQuote(): string {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86_400_000);
  return DAILY_QUOTES[dayOfYear % DAILY_QUOTES.length];
}

function relativeTime(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 2) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return new Date(isoStr).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function getFeaturedPracticeReason(
  item: PracticeLibraryItem | PracticeRecommendation | null
): string {
  if (!item) return "";
  return "reason" in item ? item.reason : item.description;
}

export function PracticePage() {
  const {
    sessions,
    stats,
    loading,
    saving,
    error,
    createSession,
    weeklyMinutes,
    practiceLibrary,
    practiceRecommendations,
    guidedPractices,
  } = usePracticeDashboard();

  const [showAddSession, setShowAddSession] = useState(false);
  const [form, setForm] = useState<CreatePracticeSessionRequest>({
    practice_type: "meditation",
    duration_minutes: 20,
    notes: "",
  });
  const [saved, setSaved] = useState(false);
  const [showFullLibrary, setShowFullLibrary] = useState(false);
  const [showAdvancedInsights, setShowAdvancedInsights] = useState(false);
  const [showDetailedTracker, setShowDetailedTracker] = useState(false);
  const [calendarView, setCalendarView] = useState<"week" | "month">("week");
  const [activeGuidedId, setActiveGuidedId] = useState<string | null>(null);
  const [timerStartedAt, setTimerStartedAt] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const recentSessions = useMemo(() => sessions.slice(0, 6), [sessions]);
  const displayedLibrary = useMemo(
    () => (showFullLibrary ? practiceLibrary : practiceLibrary.slice(0, 4)),
    [practiceLibrary, showFullLibrary]
  );
  const weeklyGoal = 150;
  const progressPercent = Math.min(Math.round((weeklyMinutes / weeklyGoal) * 100), 100);
  const experiencePoints = (stats?.total_minutes ?? 0) + (stats?.total_sessions ?? 0) * 25;
  const level = Math.max(1, Math.floor(experiencePoints / 200) + 1);
  const nextLevelXp = level * 200;
  const levelProgressPercent = Math.round(((experiencePoints % 200) / 200) * 100);
  const xpToNextLevel = nextLevelXp - (experiencePoints % 200 === 0 && experiencePoints > 0 ? 200 : experiencePoints % 200);
  const streak = stats?.current_streak ?? 0;
  const totalSessions = stats?.total_sessions ?? 0;
  const totalMinutes = stats?.total_minutes ?? 0;

  const challengeConfig = streak >= 7
    ? { minutes: 45, description: "You're in a strong rhythm. Keep it going with a deeper session." }
    : streak >= 3
    ? { minutes: 30, description: "Maintain momentum today with a steady 30-minute session." }
    : { minutes: 20, description: "Start gently with one simple session today." };

  const dailyQuote = getDailyQuote();
  const featuredPractice = practiceRecommendations[0] ?? practiceLibrary[0] ?? null;
  const featuredPracticeReason = getFeaturedPracticeReason(featuredPractice);
  const activeGuidedPractice = guidedPractices.find((item) => item.id === activeGuidedId) ?? null;
  const trackerDays = useMemo(() => buildTrackerDays(calendarView), [calendarView]);
  const trackerRange = useMemo(() => {
    if (trackerDays.length === 0) return "";
    const start = new Date(trackerDays[0].date);
    const end = new Date(trackerDays[trackerDays.length - 1].date);
    return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} - ${end.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  }, [trackerDays]);

  const practiceCellSet = useMemo(() => {
    return new Set(sessions.map((session) => `${session.practice_type}:${session.completed_at.slice(0, 10)}`));
  }, [sessions]);

  const practicedDaysInWindow = useMemo(() => {
    const trackerDaySet = new Set(trackerDays.map((day) => day.date));
    const practiced = new Set(
      sessions
        .map((session) => session.completed_at.slice(0, 10))
        .filter((date) => trackerDaySet.has(date))
    );
    return practiced.size;
  }, [sessions, trackerDays]);

  useEffect(() => {
    if (!timerStartedAt) {
      return;
    }

    const interval = window.setInterval(() => {
      const seconds = Math.max(0, Math.floor((Date.now() - timerStartedAt) / 1000));
      setElapsedSeconds(seconds);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [timerStartedAt]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await createSession(form);
    setForm({ practice_type: "meditation", duration_minutes: 20, notes: "" });
    setShowAddSession(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 4000);
  }

  function openWithPreset(type: PracticeType, minutes: number, notes = "") {
    setForm({ practice_type: type, duration_minutes: minutes, notes });
    setShowAddSession(true);
  }

  function openPracticeItem(item: Pick<PracticeLibraryItem, "practice_type" | "duration_minutes"> | PracticeRecommendation) {
    openWithPreset(item.practice_type, item.duration_minutes);
  }

  function startGuidedPractice(practiceId: string) {
    setActiveGuidedId(practiceId);
    setElapsedSeconds(0);
    setTimerStartedAt(Date.now());
  }

  function stopGuidedPractice() {
    setTimerStartedAt(null);
  }

  function logGuidedPracticeFromTimer() {
    if (!activeGuidedPractice) return;

    const minutes = Math.max(1, Math.round(elapsedSeconds / 60));
    openWithPreset(
      activeGuidedPractice.practice_type,
      minutes,
      `Guided: ${activeGuidedPractice.title}`
    );
    setTimerStartedAt(null);
  }

  if (loading) return <div className="page-loading">Loading practice dashboard…</div>;

  return (
    <div className="inner-page__container practice-page">

      {/* ── Hero ── */}
      <section className="practice-hero">
        <div className="practice-hero__copy">
          <div className="eyebrow">Sadhana Tracker</div>
          <h2 className="display-3 practice-hero__title">Your daily practice, made visible.</h2>
          <p className="practice-hero__quote">"{dailyQuote}"</p>
        </div>
        <div className="practice-hero__actions">
          <button onClick={() => setShowAddSession(true)} className="btn btn--primary practice-hero__cta">
            + Log Practice
          </button>
          <Link to="/gamification" className="btn btn--outline">
            🏅 Badge Vault
          </Link>
        </div>
      </section>

      {saved && (
        <div className="practice-save-toast" role="status">
          ✅ Session saved — great work today!
        </div>
      )}

      {error && <p className="alert alert--error">{error}</p>}

      {featuredPractice && (
        <section className="practice-panel practice-featured-panel">
          <div className="practice-panel__head">
            <div>
              <h3 className="heading-md">Featured practice</h3>
              <p className="text-muted">Your best next move, based on recent activity and momentum.</p>
            </div>
            <div className="practice-featured-panel__badge">{featuredPractice.duration_minutes} min</div>
          </div>
          <div className="practice-featured-panel__body">
            <div className="practice-featured-panel__icon" style={{ color: featuredPractice.color }}>
              {featuredPractice.icon}
            </div>
            <div className="practice-featured-panel__content">
              <h4 className="practice-featured-panel__title">{featuredPractice.title}</h4>
              <p className="practice-featured-panel__reason">{featuredPracticeReason}</p>
              <div className="practice-featured-panel__chips">
                {featuredPractice.benefits.slice(0, 3).map((benefit) => (
                  <span key={benefit} className="practice-featured-panel__chip">{benefit}</span>
                ))}
              </div>
            </div>
            <div className="practice-featured-panel__actions">
              <button onClick={() => openPracticeItem(featuredPractice)} className="btn btn--primary">
                Start now
              </button>
              <button onClick={() => setShowAddSession(true)} className="btn btn--outline">
                Log custom session
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ── Recommended practices ── */}
      <section className="practice-panel practice-module-section">
        <div className="practice-panel__head">
          <div>
            <h3 className="heading-md">Recommended for today</h3>
            <p className="text-muted">A short list of practices that fit your current rhythm.</p>
          </div>
        </div>
        <div className="practice-recommendation-grid">
          {practiceRecommendations.slice(0, 3).map((item) => (
            <button
              key={item.id}
              type="button"
              className="practice-recommendation-card"
              onClick={() => openPracticeItem(item)}
              style={{ borderColor: item.color }}
            >
              <div className="practice-recommendation-card__top">
                <span className="practice-recommendation-card__icon" style={{ color: item.color }}>{item.icon}</span>
                <span className="practice-recommendation-card__duration">{item.duration_minutes} min</span>
              </div>
              <div className="practice-recommendation-card__title">{item.title}</div>
              <p className="practice-recommendation-card__reason">{item.reason}</p>
              <div className="practice-recommendation-card__chips">
                {item.best_for.slice(0, 2).map((tag) => (
                  <span key={tag} className="practice-recommendation-card__chip">{tag}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── Practice library ── */}
      <section className="practice-panel practice-module-section">
        <div className="practice-panel__head">
          <div>
            <h3 className="heading-md">Practice library</h3>
            <p className="text-muted">Pick one when you want a clear, guided option.</p>
          </div>
        </div>
        <div className="practice-library-grid">
          {displayedLibrary.map((item) => (
            <button
              key={item.id}
              type="button"
              className="practice-library-card"
              onClick={() => openPracticeItem(item)}
              style={{ borderColor: item.color }}
            >
              <div className="practice-library-card__head">
                <span className="practice-library-card__icon" style={{ color: item.color }}>{item.icon}</span>
                <span className="practice-library-card__duration">{item.duration_minutes} min</span>
              </div>
              <div className="practice-library-card__title">{item.title}</div>
              <p className="practice-library-card__description">{item.description}</p>
              <div className="practice-library-card__meta">{item.best_for[0]}</div>
            </button>
          ))}
        </div>
        {practiceLibrary.length > 4 && (
          <div>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => setShowFullLibrary((prev) => !prev)}
            >
              {showFullLibrary ? "Show fewer options" : `Show all ${practiceLibrary.length} practices`}
            </button>
          </div>
        )}
      </section>

      {/* ── At-a-glance ── */}
      <section className="practice-panel">
        <div className="practice-panel__head">
          <div>
            <h3 className="heading-md">Today at a glance</h3>
            <p className="text-muted">{challengeConfig.description}</p>
          </div>
          <button
            onClick={() => openWithPreset("meditation", challengeConfig.minutes)}
            className="btn btn--gold btn--sm"
          >
            Start {challengeConfig.minutes} min focus
          </button>
        </div>
        <div className="practice-tracker-kpis">
          <span><strong>{streak}</strong> day streak</span>
          <span><strong>{totalSessions}</strong> sessions logged</span>
          <span><strong>{totalMinutes}</strong> minutes total</span>
        </div>
      </section>

      <section className="practice-panel">
        <div className="practice-panel__head">
          <div>
            <h3 className="heading-md">Advanced insights</h3>
            <p className="text-muted">Keep this collapsed for a simpler experience, or expand for deeper tracking.</p>
          </div>
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => setShowAdvancedInsights((prev) => !prev)}
          >
            {showAdvancedInsights ? "Hide advanced insights" : "Show advanced insights"}
          </button>
        </div>

        {showAdvancedInsights && (
          <>
            <section className="practice-gamify-grid">
              <div className="practice-panel practice-level">
                <div className="practice-level__head">
                  <span className="eyebrow">Practitioner Level</span>
                  <span className="practice-level__badge">Level {level}</span>
                </div>
                <p className="text-muted practice-level__copy">
                  <strong>{experiencePoints} XP</strong> earned - {xpToNextLevel} XP to reach Level {level + 1}
                </p>
                <div className="practice-progress-rail" title={`${levelProgressPercent}% to next level`}>
                  <div className="practice-progress-fill" style={{ width: `${levelProgressPercent}%` }} />
                </div>
              </div>

              <div className="practice-panel practice-quest">
                <span className="eyebrow">Today's Challenge</span>
                <div className="practice-quest__pill">{streak >= 7 ? "Advanced" : streak >= 3 ? "Momentum" : "Starter"}</div>
                <h3 className="heading-md practice-quest__title">{challengeConfig.description}</h3>
                <button
                  onClick={() => openWithPreset("meditation", challengeConfig.minutes)}
                  className="btn btn--gold btn--sm practice-quest__btn"
                >
                  Accept Challenge
                </button>
              </div>
            </section>

            <section className="practice-metric-grid">
              <MetricCard icon="🔥" title="Current streak" value={`${streak}`} unit="days" accentClass="practice-metric--fire" />
              <MetricCard icon="🏆" title="Longest streak" value={`${stats?.longest_streak ?? 0}`} unit="days" accentClass="practice-metric--teal" />
              <MetricCard icon="⏱️" title="Total minutes" value={`${stats?.total_minutes ?? 0}`} unit="min" accentClass="practice-metric--blue" />
              <MetricCard icon="📋" title="Sessions logged" value={`${stats?.total_sessions ?? 0}`} unit="sessions" accentClass="practice-metric--violet" />
            </section>
          </>
        )}
      </section>

      {/* ── Weekly progress ── */}
      <section className="practice-panel">
        <div className="practice-panel__head">
          <div>
            <h3 className="heading-md">Weekly goal</h3>
            <p className="text-muted">{weeklyMinutes} of {weeklyGoal} minutes this week</p>
          </div>
          <div className={`practice-progress-value${progressPercent >= 100 ? " practice-progress-value--done" : ""}`}>
            {progressPercent >= 100 ? "✅ Done!" : `${progressPercent}%`}
          </div>
        </div>
        <div className="practice-progress-rail" title={`${progressPercent}% of weekly goal`}>
          <div className="practice-progress-fill practice-progress-fill--warm" style={{ width: `${progressPercent}%` }} />
        </div>
        {progressPercent < 100 && (
          <p className="practice-weekly-hint">
            {weeklyGoal - weeklyMinutes} more minutes will complete your weekly goal.
          </p>
        )}
      </section>

      <section className="practice-panel">
        <div className="practice-panel__head">
          <div>
            <h3 className="heading-md">Guided timed practices</h3>
            <p className="text-muted">Use start/stop controls to follow a guided sequence and log the exact time practiced.</p>
          </div>
        </div>

        {activeGuidedPractice && (
          <div className="practice-panel" style={{ marginBottom: "0.75rem", background: "#fffaf2" }}>
            <h4 className="heading-md" style={{ marginBottom: "0.35rem" }}>{activeGuidedPractice.title}</h4>
            <p className="text-muted" style={{ marginBottom: "0.5rem" }}>{activeGuidedPractice.description}</p>
            <p style={{ marginBottom: "0.5rem", fontWeight: 600 }}>
              Elapsed: {Math.floor(elapsedSeconds / 60).toString().padStart(2, "0")}:{(elapsedSeconds % 60).toString().padStart(2, "0")}
            </p>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {!timerStartedAt ? (
                <button type="button" className="btn btn--primary btn--sm" onClick={() => startGuidedPractice(activeGuidedPractice.id)}>
                  Start
                </button>
              ) : (
                <button type="button" className="btn btn--outline btn--sm" onClick={stopGuidedPractice}>
                  Stop
                </button>
              )}
              <button type="button" className="btn btn--gold btn--sm" onClick={logGuidedPracticeFromTimer}>
                Log timed session
              </button>
            </div>
            <ol style={{ marginTop: "0.75rem", paddingLeft: "1.1rem" }}>
              {activeGuidedPractice.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        )}

        <div className="practice-library-grid">
          {guidedPractices.map((item) => (
            <button
              key={item.id}
              type="button"
              className="practice-library-card"
              onClick={() => setActiveGuidedId(item.id)}
              style={{ borderColor: item.id === activeGuidedId ? "var(--c-brand)" : undefined }}
            >
              <div className="practice-library-card__head">
                <span className="practice-library-card__icon">🎧</span>
                <span className="practice-library-card__duration">{item.duration_minutes} min</span>
              </div>
              <div className="practice-library-card__title">{item.title}</div>
              <p className="practice-library-card__description">{item.description}</p>
              <div className="practice-library-card__meta" style={{ textTransform: "capitalize" }}>
                {item.practice_type}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── Recent sessions ── */}
      <section className="practice-panel">
        <div className="practice-panel__head">
          <div>
            <h3 className="heading-md">Recent sessions</h3>
            <p className="text-muted">Simple history of what you have already completed.</p>
          </div>
          <button onClick={() => setShowAddSession(true)} className="btn btn--outline btn--sm">
            + Add session
          </button>
        </div>
        <aside className="practice-session-rail">
          {recentSessions.length === 0 ? (
            <div className="practice-empty-state">
              <div className="practice-empty-state__icon">🌱</div>
              <p className="practice-empty-state__title">No sessions yet</p>
              <button onClick={() => setShowAddSession(true)} className="btn btn--primary btn--sm">
                Log first session
              </button>
            </div>
          ) : (
            <ul className="practice-session-list">
              {recentSessions.map((session) => {
                const pt = PRACTICE_TYPES.find((p) => p.value === session.practice_type);
                return (
                  <li key={session.id} className="practice-session-row">
                    <div className="practice-session-icon" style={{ color: pt?.color }}>
                      {pt?.icon ?? "✨"}
                    </div>
                    <div className="practice-session-body">
                      <div className="practice-session-type">{pt?.label ?? session.practice_type}</div>
                      {session.notes && <div className="practice-session-notes">{session.notes}</div>}
                    </div>
                    <div className="practice-session-right">
                      <div className="practice-session-minutes">{session.duration_minutes} min</div>
                      <div className="practice-session-when">{relativeTime(session.completed_at)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </section>

      <section className="practice-panel">
        <div className="practice-panel__head">
          <div>
            <h3 className="heading-md">Detailed activity tracker</h3>
            <p className="text-muted">Original detailed view with week/month tracking by practice type.</p>
          </div>
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => setShowDetailedTracker((prev) => !prev)}
          >
            {showDetailedTracker ? "Hide detailed tracker" : "Show detailed tracker"}
          </button>
        </div>

        {showDetailedTracker && (
          <>
            <div className="practice-tracker-controls">
              <div className="practice-tracker-tabs" role="tablist" aria-label="Tracker range">
                <button
                  type="button"
                  role="tab"
                  aria-selected={calendarView === "week"}
                  className={`practice-tracker-tab${calendarView === "week" ? " is-active" : ""}`}
                  onClick={() => setCalendarView("week")}
                >
                  Week
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={calendarView === "month"}
                  className={`practice-tracker-tab${calendarView === "month" ? " is-active" : ""}`}
                  onClick={() => setCalendarView("month")}
                >
                  Month
                </button>
              </div>
              <div className="practice-tracker-range">{trackerRange}</div>
            </div>

            <div className="practice-tracker-kpis">
              <span>
                <strong>{practicedDaysInWindow}</strong> / {trackerDays.length} days practiced
              </span>
              <span>
                <strong>{Math.round((practicedDaysInWindow / Math.max(trackerDays.length, 1)) * 100)}%</strong> consistency
              </span>
            </div>

            <div className="practice-tracker-layout">
              <div className="practice-tracker-board-wrap">
                <div className="practice-tracker-board" style={{ "--tracker-cols": trackerDays.length } as React.CSSProperties}>
                  <div className="practice-tracker-board__header">
                    <div className="practice-tracker-board__type-head">Practice</div>
                    {trackerDays.map((day) => (
                      <div key={day.date} className="practice-tracker-board__day-head" title={day.date}>
                        <div>{day.weekday}</div>
                        <div className="practice-tracker-board__date">{day.day}</div>
                      </div>
                    ))}
                    <div className="practice-tracker-board__ratio-head">Done</div>
                  </div>

                  {PRACTICE_TYPES.map((type) => {
                    const doneCount = trackerDays.reduce(
                      (sum, day) => sum + (practiceCellSet.has(`${type.value}:${day.date}`) ? 1 : 0),
                      0
                    );

                    return (
                      <div key={type.value} className="practice-tracker-board__row">
                        <div className="practice-tracker-board__type">
                          <span className="practice-tracker-board__type-icon" style={{ color: type.color }}>{type.icon}</span>
                          <span>{type.label}</span>
                        </div>

                        {trackerDays.map((day) => {
                          const active = practiceCellSet.has(`${type.value}:${day.date}`);
                          return (
                            <div
                              key={`${type.value}:${day.date}`}
                              className={`practice-tracker-cell${active ? " is-active" : ""}${day.isToday ? " is-today" : ""}`}
                              style={active ? ({ "--cell-color": type.color } as React.CSSProperties) : undefined}
                              title={active ? `${type.label}: completed on ${day.date}` : `${type.label}: not logged on ${day.date}`}
                            />
                          );
                        })}

                        <div className="practice-tracker-board__ratio">{doneCount}/{trackerDays.length}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="practice-activity-legend">
                  <span className="practice-calendar-legend__dot practice-calendar-legend__dot--active" /> Completed
                  <span className="practice-calendar-legend__dot" style={{ marginLeft: "0.75rem" }} /> Missed
                  <span className="practice-calendar-legend__dot practice-calendar-legend__dot--today" style={{ marginLeft: "0.75rem" }} /> Today
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ── Log session modal ── */}
      {showAddSession && (
        <div
          className="practice-modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setShowAddSession(false); }}
        >
          <form onSubmit={handleSubmit} className="practice-modal" aria-label="Log practice session">
            <div className="practice-modal__header">
              <h3 className="heading-md">Log a practice session</h3>
              <button
                type="button"
                onClick={() => setShowAddSession(false)}
                className="practice-modal__close"
                aria-label="Close"
              >×</button>
            </div>

            <div className="practice-modal__fields">
              <fieldset className="practice-modal__type-fieldset">
                <legend className="practice-modal__field-label">What did you practice?</legend>
                <div className="practice-modal__type-grid">
                  {PRACTICE_TYPES.map((pt) => (
                    <label
                      key={pt.value}
                      className={`practice-type-chip${form.practice_type === pt.value ? " is-selected" : ""}`}
                      style={{ "--pt-color": pt.color } as React.CSSProperties}
                    >
                      <input
                        type="radio"
                        name="practice_type"
                        value={pt.value}
                        checked={form.practice_type === pt.value}
                        onChange={() => setForm((prev) => ({ ...prev, practice_type: pt.value }))}
                        className="sr-only"
                      />
                      <span className="practice-type-chip__icon">{pt.icon}</span>
                      <span className="practice-type-chip__label">{pt.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <label htmlFor="duration" className="practice-modal__field-label">
                  Duration (minutes)
                </label>
                <div className="practice-modal__duration-row">
                  {QUICK_DURATIONS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`practice-duration-chip${form.duration_minutes === d ? " is-selected" : ""}`}
                      onClick={() => setForm((prev) => ({ ...prev, duration_minutes: d }))}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <input
                  id="duration"
                  type="number"
                  min={1}
                  value={form.duration_minutes}
                  onChange={(e) => setForm((prev) => ({ ...prev, duration_minutes: Number(e.target.value) || 1 }))}
                  style={{ ...fieldStyle, marginTop: "0.5rem" }}
                  aria-label="Custom duration in minutes"
                />
              </div>

              <div>
                <label htmlFor="notes" className="practice-modal__field-label">
                  Reflection <span className="practice-modal__optional">(optional)</span>
                </label>
                <textarea
                  id="notes"
                  value={form.notes ?? ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="How did your practice feel? What arose? What settled?"
                  style={{ ...fieldStyle, resize: "vertical" }}
                />
              </div>
            </div>

            <div className="practice-modal__actions">
              <button type="submit" disabled={saving} className="btn btn--primary">
                {saving ? "Saving…" : "Save session"}
              </button>
              <button type="button" onClick={() => setShowAddSession(false)} className="btn btn--outline">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function buildTrackerDays(view: "week" | "month") {
  const dayCount = view === "week" ? 7 : 28;
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const days = [] as Array<{ date: string; day: string; weekday: string; isToday: boolean }>;

  for (let index = dayCount - 1; index >= 0; index -= 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    const iso = date.toISOString().slice(0, 10);
    days.push({
      date: iso,
      day: String(date.getDate()),
      weekday: date.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 3),
      isToday: iso === todayIso,
    });
  }

  return days;
}

function MetricCard({
  icon,
  title,
  value,
  unit,
  accentClass,
}: {
  icon: string;
  title: string;
  value: string;
  unit: string;
  accentClass: string;
}) {
  return (
    <div className={`practice-metric ${accentClass}`}>
      <div className="practice-metric__icon">{icon}</div>
      <div className="practice-metric__value">{value}</div>
      <div className="practice-metric__unit">{unit}</div>
      <div className="practice-metric__title">{title}</div>
    </div>
  );
}
